import { useAtom } from 'jotai';
import { Dialog } from 'primereact/dialog';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FundGetData } from '~/api/def/endpoints/fundEndpoint';
import { globalSearchStringAtom } from '~/atoms/globalSearch';
import { GlobalSearchTabs, SearchCategory } from './GlobalSearchTabs';
import { SearchBox } from './components/SearchBox';
import { SearchContent } from './components/SearchContent';
import { executeSearch } from './services/searchService';
import { ExtendedDocCompareChunkFinderChunk, Widget } from './types';

interface GlobalSearchPopupProps {
  onSearchCallback: () => void;
  onFundSelect?: (fund: FundGetData) => void;
  initialActiveCategory?: SearchCategory;
}

export type GlobalSearchPopupRef = {
  show: (category?: SearchCategory) => void;
  hide: () => void;
};

interface SearchResults {
  funds: ExtendedDocCompareChunkFinderChunk[];
  portfolios: ExtendedDocCompareChunkFinderChunk[];
  documents: ExtendedDocCompareChunkFinderChunk[];
  widgets: Widget[];
}

interface LoadingState {
  funds: boolean;
  portfolios: boolean;
  documents: boolean;
  widgets: boolean;
}

export const GlobalSearchPopup = forwardRef<
  GlobalSearchPopupRef,
  GlobalSearchPopupProps
>(({ onFundSelect, initialActiveCategory }: GlobalSearchPopupProps, ref) => {
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useAtom(globalSearchStringAtom);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    funds: false,
    portfolios: false,
    documents: false,
    widgets: false,
  });
  const [activeCategory, setActiveCategory] = useState<SearchCategory>(
    initialActiveCategory || 'All',
  );
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Store results for each type separately
  const [results, setResults] = useState<SearchResults>({
    funds: [],
    portfolios: [],
    documents: [],
    widgets: [],
  });
  const [isEmptyResult] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compute if any search is still loading
  const isAnyLoading = useMemo(() => {
    return Object.values(loadingState).some((loading) => loading);
  }, [loadingState]);

  // Combine all results
  const allResults = useMemo(() => {
    return [
      ...results.funds,
      ...results.portfolios,
      ...results.documents,
      ...results.widgets,
    ];
  }, [results]);

  // Filter results based on active category
  const filteredResults = useMemo(() => {
    if (activeCategory === 'All') {
      return allResults;
    }

    if (activeCategory === 'Widgets') {
      return results.widgets;
    }

    const categoryTypeMap: Record<SearchCategory, string[]> = {
      All: [],
      Funds: ['Funds', 'fund'],
      Portfolios: ['Portfolios', 'portfolio'],
      Documents: ['Documents', 'document'],
      Widgets: ['widget'],
    };

    const validTypes = categoryTypeMap[activeCategory] || [];
    return allResults.filter(
      (item): item is ExtendedDocCompareChunkFinderChunk => {
        if ('type' in item && item.type === 'widget') {
          return false;
        }
        const itemType = item.type?.toLowerCase() || '';
        const docType =
          'document_type' in item
            ? item.document_type?.toLowerCase()
            : undefined;
        return validTypes.some(
          (type) =>
            itemType === type.toLowerCase() ||
            (docType && docType === type.toLowerCase()),
        );
      },
    );
  }, [allResults, activeCategory, results.widgets]);

  useImperativeHandle(
    ref,
    () => ({
      show: (category?: SearchCategory) => {
        if (category) {
          setActiveCategory(category);
        }
        setVisible(true);
        if (allResults.length === 0) {
          setSearch('');
          // Load only widgets initially
          handleSearch('', true);
        }
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      },
      hide: () => setVisible(false),
    }),
    [allResults.length, setSearch],
  );

  const handleSearch = async (
    searchTerm: string,
    widgetsOnly: boolean = false,
  ) => {
    if (!searchTerm.trim() && !widgetsOnly) {
      setResults({ funds: [], portfolios: [], documents: [], widgets: [] });
      setLoadingState({
        funds: false,
        portfolios: false,
        documents: false,
        widgets: false,
      });
      return;
    }

    setError(null);
    setLoadingState({
      funds: !widgetsOnly,
      portfolios: !widgetsOnly,
      documents: !widgetsOnly,
      widgets: true,
    });

    if (!widgetsOnly) {
      setResults({ funds: [], portfolios: [], documents: [], widgets: [] });
    }

    try {
      await executeSearch(
        searchTerm,
        'All',
        {
          onFundsSuccess: (
            fundResults: ExtendedDocCompareChunkFinderChunk[],
          ) => {
            if (!widgetsOnly) {
              setResults((prev) => ({ ...prev, funds: fundResults }));
              setLoadingState((prev) => ({ ...prev, funds: false }));
            }
          },
          onPortfoliosSuccess: (
            portfolioResults: ExtendedDocCompareChunkFinderChunk[],
          ) => {
            if (!widgetsOnly) {
              setResults((prev) => ({
                ...prev,
                portfolios: portfolioResults,
              }));
              setLoadingState((prev) => ({ ...prev, portfolios: false }));
            }
          },
          onDocumentsSuccess: (
            documentResults: ExtendedDocCompareChunkFinderChunk[],
          ) => {
            if (!widgetsOnly) {
              setResults((prev) => ({ ...prev, documents: documentResults }));
              setLoadingState((prev) => ({ ...prev, documents: false }));
            }
          },
          onWidgetsSuccess: (widgetResults: Widget[]) => {
            setResults((prev) => ({ ...prev, widgets: widgetResults }));
            setLoadingState((prev) => ({ ...prev, widgets: false }));
          },
          onError: (error: Error) => {
            setError(error.message);
            setLoadingState({
              funds: false,
              portfolios: false,
              documents: false,
              widgets: false,
            });
          },
        },
        widgetsOnly,
      );
    } catch (error) {
      setError(
        error instanceof Error ? error.message : 'An unexpected error occurred',
      );
      setLoadingState({
        funds: false,
        portfolios: false,
        documents: false,
        widgets: false,
      });
    }
  };

  const handleCategoryChange = (category: SearchCategory) => {
    setActiveCategory(category);
  };

  // Define consistent padding value
  const horizontalPadding = '16px';

  const showEmptyState =
    !isAnyLoading &&
    !error &&
    search.trim() !== '' && // Only show empty state if there's a search term
    (isEmptyResult || (filteredResults && filteredResults.length === 0));

  return (
    <Dialog
      onHide={() => setVisible(false)}
      visible={visible}
      position="top"
      header={null}
      showHeader={false}
      closable={false}
      style={{
        width: '80vw',
        maxHeight: '80vh',
        overflow: 'hidden',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
      }}
      contentClassName="p-0"
      data-testid="global-search-popup"
    >
      <div
        className="p-0 global-search-container"
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--bq-surface-card)',
        }}
      >
        {/* Fixed header section */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'var(--bq-surface-card)',
            zIndex: 1,
          }}
        >
          {/* Search box at the top */}
          <SearchBox
            search={search}
            setSearch={setSearch}
            onSearch={() => handleSearch(search)}
            onClose={() => setVisible(false)}
            searchInputRef={searchInputRef}
            horizontalPadding={horizontalPadding}
          />

          {/* Tabs below search box */}
          <div
            style={{
              marginLeft: horizontalPadding,
              marginRight: horizontalPadding,
              marginBottom: '4px',
            }}
          >
            <GlobalSearchTabs
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
            />
          </div>
        </div>

        {/* Scrollable content area */}
        <div
          className="p-3"
          style={{
            backgroundColor: 'var(--bq-surface-card)',
            overflowY: 'auto',
            flex: 1,
            marginLeft: horizontalPadding,
            marginRight: horizontalPadding,
            marginBottom: horizontalPadding,
            borderRadius: '0 0 4px 4px',
          }}
        >
          <SearchContent
            error={error}
            showEmptyState={showEmptyState}
            search={search}
            filteredResults={filteredResults}
            isAnyLoading={isAnyLoading}
            activeCategory={activeCategory}
            results={results}
            onWidgetClick={() => {
              setVisible(false);
            }}
            onFundSelect={onFundSelect}
            onClose={() => setVisible(false)}
          />
        </div>
      </div>
    </Dialog>
  );
});
